import { FC, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { Button, Modal, Popconfirm, Space, Table, Tag, Typography } from "antd"
import { UploadComponent } from "components"
import { useStore } from "stores/utils/use-store"
import { SupportedGame } from "../store/types"

const ICON_SIZE = { width: 40, height: 40, objectFit: "contain" as const }

export const GameplayBoostGames: FC = observer(() => {
  const { dataStore: { gameplayBoostGamesStore: store, filesStore } } = useStore()
  const [editing, setEditing] = useState<SupportedGame | null>(null)
  const [picture, setPicture] = useState("")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    store.loadGames()
  }, [store])

  const openEditor = (game: SupportedGame) => {
    setEditing(game)
    setPicture(game.pictureUrl ?? "")
  }

  const savePicture = async(gameId: number, pictureUrl: string | null) => {
    setSaving(true)
    const saved = await store.setPicture(gameId, pictureUrl)
    setSaving(false)

    return saved
  }

  const onSave = async() => {
    if (!editing) return
    const { gameId } = editing
    if (await savePicture(gameId, picture.trim() || null)) {
      setEditing(current => (current?.gameId === gameId ? null : current))
    }
  }

  const columns = [
    {
      title: "Icon",
      key: "pictureUrl",
      width: 80,
      render: (_: unknown, game: SupportedGame) => (game.pictureUrl ? <img src={game.pictureUrl} alt="" style={ICON_SIZE} /> : "—"),
    },
    { title: "Game ID", dataIndex: "gameId", key: "gameId", width: 100 },
    {
      title: "Name",
      key: "gameName",
      render: (_: unknown, game: SupportedGame) => game.gameName ?? <Typography.Text type="secondary">No name</Typography.Text>,
    },
    {
      title: "Status",
      key: "enabled",
      width: 110,
      render: (_: unknown, game: SupportedGame) => (game.enabled ? <Tag color="green">Enabled</Tag> : <Tag>Disabled</Tag>),
    },
    {
      title: "Actions",
      key: "actions",
      width: 220,
      render: (_: unknown, game: SupportedGame) => (
        <Space>
          <Button onClick={() => openEditor(game)}>Change icon</Button>
          {game.pictureUrl && (
            <Popconfirm
              title="Clear the icon? The app falls back to its built-in logo, or a gamepad."
              onConfirm={() => savePicture(game.gameId, null)}
            >
              <Button danger disabled={saving}>Clear icon</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  return (
    <>
      <Typography.Paragraph type="secondary">
        Icons shown on the Recordings page and the Supported Games list in the app, for games that pay a Gameplay Boost.
        They replace the app&apos;s built-in logo and show on the next page load.
      </Typography.Paragraph>
      <Table
        dataSource={store.games}
        loading={store.isLoading}
        columns={columns}
        rowKey="gameId"
        pagination={false}
      />
      <Modal
        open={editing !== null}
        title={editing ? `Icon for ${editing.gameName ?? `game ${editing.gameId}`}` : ""}
        okText="Save"
        onOk={onSave}
        confirmLoading={saving}
        okButtonProps={{ disabled: filesStore.isLoading }}
        onCancel={() => setEditing(null)}
        destroyOnClose
      >
        <UploadComponent image={picture} setImage={setPicture} title="SVG or PNG, square" imagesOnly />
      </Modal>
    </>
  )
})
